import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, Loader2, UserRoundCheck } from 'lucide-react';
import { BoardToolbarExpandSearch } from '../../board/toolbar/BoardToolbarExpandSearch';
import { technicianInitials } from '../../board/utils/technicianInitials';
import { useUserProfilePhotos } from '../../user-management/hooks/useUserProfilePhotos';
import { Ci2HelpHexIcon } from '../../process-config/components/Ci2HelpHexIcon';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../process-config/ConfigStatusView2.css';
import '../../process-config/components/CondicoesIntervencaoTableV2.css';
import '../../customers-chat/components/CustomersChatView.css';
import './CustomersCadastroPermissoesView.css';

/** Habilitados primeiro (abertos); Não habilitados depois (recolhidos). */
const GROUPS = [
  {
    key: 'habilitados',
    label: 'Habilitados',
    desc: 'Usuários com permissão ativada',
    color: '#0e8a82',
  },
  {
    key: 'naoHabilitados',
    label: 'Não habilitados',
    desc: 'Usuários com permissão ainda não ativada',
    color: '#767889',
  },
];

const ONLY_REG_HELP =
  'Ao abrir o sistema, visualizar somente a tela de cadastros';

function normalizeSearch(v) {
  return String(v ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

async function apiVc(path, { method = 'GET', body } = {}) {
  const token = localStorage.getItem('authToken');
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
  };
  const res = await fetch(`/api/vc-usr${path}`, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    throw new Error(json.message || `Erro ${res.status}`);
  }
  return json;
}

function Ci2Switch({ checked, disabled, onChange, tooltip }) {
  const switchBtn = (
    <button
      type="button"
      className={`ci2-switch${checked ? ' on' : ''}`}
      disabled={disabled}
      aria-pressed={checked}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled && onChange) onChange(!checked);
      }}
    />
  );
  if (!tooltip) return switchBtn;
  return (
    <span className="ci2-switch-tooltip-wrap ci2-modern-tooltip-host" data-tooltip={tooltip}>
      {switchBtn}
    </span>
  );
}

function UserAvatar({ user, photoMap, size = 22 }) {
  const photo =
    photoMap && user?.id != null ? String(photoMap[String(user.id)] || '').trim() : '';
  const label = String(user?.name || '').trim();
  if (photo) {
    return (
      <span
        className="vch-user-picker-avatar vch-user-picker-avatar--photo vch-agents-teams-avatar"
        style={{ width: size, height: size }}
        aria-hidden
      >
        <img src={photo} alt="" className="vch-user-picker-avatar-img" />
      </span>
    );
  }
  return (
    <span
      className="vch-user-picker-avatar vch-user-picker-avatar--initials vch-agents-teams-avatar"
      style={{ width: size, height: size, fontSize: Math.max(8, Math.round(size * 0.38)) }}
      aria-hidden
    >
      {technicianInitials(label)}
    </span>
  );
}

/**
 * Processos → Cadastro de usuários — permissões (customers_users_reg / _only).
 */
export default function CustomersCadastroPermissoesView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  /** Início: Habilitados aberto; Não habilitados recolhido. */
  const [collapsed, setCollapsed] = useState({ naoHabilitados: true });
  const [busyId, setBusyId] = useState(null);
  const [onlyRegHelpOpen, setOnlyRegHelpOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const j = await apiVc('/cadastro-permissoes');
      setRows(Array.isArray(j.data) ? j.data : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar usuários');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const photoMap = useUserProfilePhotos(rows);

  const search = normalizeSearch(searchQuery);

  const filtered = useMemo(() => {
    if (!search) return rows;
    return rows.filter((r) => {
      const blob = normalizeSearch(`${r.name} ${r.email} ${r.type}`);
      return blob.includes(search);
    });
  }, [rows, search]);

  const grouped = useMemo(() => {
    const habilitados = [];
    const naoHabilitados = [];
    for (const r of filtered) {
      if (r.enabled) habilitados.push(r);
      else naoHabilitados.push(r);
    }
    return { habilitados, naoHabilitados };
  }, [filtered]);

  const visibleGroups = useMemo(
    () =>
      GROUPS.filter((g) => {
        const list = grouped[g.key] || [];
        if (search) return list.length > 0;
        return true;
      }),
    [grouped, search]
  );

  const allCollapsed =
    !search &&
    visibleGroups.length > 0 &&
    visibleGroups.every((g) => !!collapsed[g.key]);

  const toggleGroup = (key) => {
    if (search) return;
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const setEnabled = async (user, nextEnabled) => {
    if (!user?.id || busyId != null) return;
    setBusyId(user.id);
    const prev = rows;
    setRows((list) =>
      list.map((r) =>
        r.id === user.id
          ? { ...r, enabled: nextEnabled, onlyReg: nextEnabled ? r.onlyReg : false }
          : r
      )
    );
    try {
      await apiVc(`/cadastro-permissoes/${user.id}`, {
        method: 'PATCH',
        body: { enabled: nextEnabled },
      });
    } catch (e) {
      setRows(prev);
      setError(e instanceof Error ? e.message : 'Erro ao atualizar permissão');
    } finally {
      setBusyId(null);
    }
  };

  const setOnlyReg = async (user, nextOnly) => {
    if (!user?.id || busyId != null) return;
    setBusyId(user.id);
    const prev = rows;
    setRows((list) =>
      list.map((r) =>
        r.id === user.id
          ? {
              ...r,
              onlyReg: nextOnly,
              enabled: nextOnly ? true : r.enabled,
            }
          : r
      )
    );
    try {
      await apiVc(`/cadastro-permissoes/${user.id}`, {
        method: 'PATCH',
        body: { onlyReg: nextOnly },
      });
    } catch (e) {
      setRows(prev);
      setError(e instanceof Error ? e.message : 'Erro ao atualizar somente cadastro');
    } finally {
      setBusyId(null);
    }
  };

  const showTable = !loading && rows.length > 0;
  const noSearchHits =
    showTable && search && grouped.habilitados.length === 0 && grouped.naoHabilitados.length === 0;

  return (
    <section className="pcs2-section cp-cad-perm-section">
      <div className="pcs2-section-aside">
        <div className="pcs2-section-heading">
          <UserRoundCheck size={16} strokeWidth={1.75} aria-hidden />
          <h3 className="pcs2-section-title">Usuários com permissão</h3>
        </div>
        <p className="pcs2-section-desc">
          Pesquise e habilite permissão para ver notificações de cadastro
        </p>
      </div>

      <div className="pcs2-section-body">
        {error ? <p className="vch-error">{error}</p> : null}

        <div className="pcs2-subblock">
          <div className="pcs2-subhead pcs2-subhead--with-action">
            <div className="pcs2-subhead-copy cp-cad-perm-subhead-copy">
              <div className="pcs2-section-heading">
                <h3 className="pcs2-section-title">Permissões de cadastro</h3>
              </div>
              <p className="pcs2-section-desc">
                Habilite quem pode ver e tratar notificações de cadastro pendente.
              </p>
            </div>
            <div className="pcs2-subhead-action">
              <BoardToolbarExpandSearch
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Pesquisar usuários…"
                ariaLabel="Pesquisar usuários"
                forceExpanded
              />
            </div>
          </div>

          <div
            className={[
              'ci2-root',
              'cp-cad-perm-ci2',
              allCollapsed ? 'cp-cad-perm-ci2--compact' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <div className="ci2-panel">
              {loading ? (
                <div className="ci2-foot-note">
                  <Loader2 size={16} className="vch-spin" aria-hidden /> Carregando…
                </div>
              ) : !showTable ? (
                <div className="ci2-foot-note">Nenhum usuário disponível.</div>
              ) : noSearchHits ? (
                <div className="ci2-foot-note">Nenhum resultado para a pesquisa.</div>
              ) : (
                <div
                  className={[
                    'ci2-table-scroll',
                    'cp-cad-perm-scroll',
                    allCollapsed ? 'cp-cad-perm-scroll--compact' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <table className="ci2-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Nome</th>
                        <th>E-mail</th>
                        <th>Tipo</th>
                        <th className={onlyRegHelpOpen ? 'ci2-th-has-help-open' : ''}>
                          <span className="ci2-th-inner ci2-th-has-help">
                            Somente cadastro
                            <Ci2HelpHexIcon
                              label="Ajuda sobre Somente cadastro"
                              expanded={onlyRegHelpOpen}
                              onToggle={() => setOnlyRegHelpOpen((v) => !v)}
                            />
                            {onlyRegHelpOpen ? (
                              <div
                                className="ci2-th-help-popover ci2-th-help-popover--align-end"
                                role="tooltip"
                              >
                                {ONLY_REG_HELP}
                              </div>
                            ) : null}
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {GROUPS.map((group) => {
                        const list = grouped[group.key] || [];
                        if (!list.length && search) return null;
                        if (!list.length && rows.length === 0) return null;
                        const isCollapsed = search ? false : !!collapsed[group.key];
                        const catClass =
                          group.key === 'habilitados' ? 'cat-nada' : 'cat-desabilitada';
                        return (
                          <React.Fragment key={group.key}>
                            <tr
                              className={`ci2-group-row${isCollapsed ? ' collapsed' : ''}`}
                              onClick={() => toggleGroup(group.key)}
                            >
                              <td colSpan={5}>
                                <div className="ci2-group-label">
                                  <span className="ci2-chev">
                                    <ChevronDown size={13} strokeWidth={2.5} />
                                  </span>
                                  <span
                                    className="ci2-dot"
                                    style={{ background: group.color }}
                                    aria-hidden
                                  />
                                  {group.label}
                                  <span className="ci2-group-count">· {list.length}</span>
                                  <span className="ci2-group-desc">{group.desc}</span>
                                </div>
                              </td>
                            </tr>
                            {!isCollapsed
                              ? list.map((user) => {
                                  const busy = busyId === user.id;
                                  return (
                                    <tr
                                      key={user.id}
                                      className={`ci2-data-row ${catClass}`}
                                      style={{ '--vch-team-accent': group.color }}
                                    >
                                      <td className="ci2-td ci2-td-center ci2-td-permite">
                                        {user.enabled ? (
                                          <button
                                            type="button"
                                            className="ci2-lock-toggle on ci2-modern-tooltip-host"
                                            data-tooltip="Desabilitar permissão"
                                            aria-label={`Desabilitar ${user.name}`}
                                            aria-pressed="true"
                                            disabled={busyId != null}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setEnabled(user, false);
                                            }}
                                          >
                                            <span className="ci2-knob">
                                              {busy ? null : (
                                                <Check size={7} strokeWidth={3} aria-hidden />
                                              )}
                                            </span>
                                          </button>
                                        ) : busy ? (
                                          <span className="ci2-enabling">
                                            <span className="ci2-enabling-spinner" aria-hidden />
                                          </span>
                                        ) : (
                                          <button
                                            type="button"
                                            className="ci2-enable-affordance ci2-modern-tooltip-host"
                                            data-tooltip="Habilitar permissão de cadastro"
                                            aria-label={`Habilitar ${user.name}`}
                                            disabled={busyId != null}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setEnabled(user, true);
                                            }}
                                          >
                                            <svg
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth="2.2"
                                              width="14"
                                              height="14"
                                              aria-hidden
                                            >
                                              <path d="M12 5v14M5 12h14" />
                                            </svg>
                                          </button>
                                        )}
                                      </td>
                                      <td className="ci2-td cp-cad-perm-td-nome" title={user.name}>
                                        <span className="vch-agents-teams-name">
                                          <UserAvatar user={user} photoMap={photoMap} />
                                          <span className="vch-agents-teams-name-text">
                                            {user.name}
                                          </span>
                                        </span>
                                      </td>
                                      <td
                                        className="ci2-td cp-cad-perm-td-email"
                                        title={user.email}
                                      >
                                        <span className="vch-agents-teams-name-text">
                                          {user.email}
                                        </span>
                                      </td>
                                      <td className="ci2-td cp-cad-perm-td-tipo">
                                        <span className="vch-agents-teams-name-text">
                                          {user.type}
                                        </span>
                                      </td>
                                      <td className="ci2-td ci2-td-center cp-cad-perm-td-only">
                                        <Ci2Switch
                                          checked={!!user.onlyReg}
                                          disabled={busyId != null}
                                          onChange={(next) => setOnlyReg(user, next)}
                                          tooltip={
                                            user.onlyReg
                                              ? 'Desabilitar somente cadastro'
                                              : 'Habilitar somente cadastro'
                                          }
                                        />
                                      </td>
                                    </tr>
                                  );
                                })
                              : null}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
